import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { FaUser, FaEdit, FaSave, FaTimes, FaCamera, FaEnvelope, FaBirthdayCake, FaBullseye, FaBook, FaArrowLeft, FaPenFancy, FaFlag } from 'react-icons/fa';
import toast from 'react-hot-toast';
import UserService from '../services/UserService';
import AuthService from '../services/AuthService';
import ReflectionService from '../services/ReflectionService';
import { isGuestUser } from '../utils/guestUtils';
import soundEffects from '../utils/soundEffects';
import { createRipple, createPulse, createSparkle, createGlow, createSuccessBurst } from '../utils/vfx';
import './Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const { state, actions } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    username: '',
    email: '',
    ageRange: '',
    financialGoal: '',
    learningMode: 'facts',
    profilePictureUrl: ''
  });
  const [originalData, setOriginalData] = useState({});
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  // Money Bio — reflections written during interactive workshops
  const [reflections, setReflections] = useState([]);

  useEffect(() => {
    let cancelled = false;
    ReflectionService.getMine()
      .then((data) => {
        if (!cancelled) setReflections(data.reflections || []);
      })
      .catch(() => {
        // Money Bio is optional — fail silently
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const isGuest = isGuestUser(state.user);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isAuth = AuthService.isAuthenticated();
        if (!isAuth || !state.user) {
          navigate('/login', { replace: true });
          return;
        }
        await loadProfile();
        soundEffects.playPageTransition();
      } catch (error) {
        toast.error('Failed to load profile');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, state.user]);

  const loadProfile = async () => {
    try {
      const response = await UserService.getProfile();
      const user = response.user || state.user;
      
      const data = {
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        ageRange: user.ageRange || '',
        financialGoal: user.financialGoal || '',
        learningMode: user.learningMode || 'facts',
        profilePictureUrl: user.profilePictureUrl || ''
      };

      setProfileData(data);
      setOriginalData(data);
      
      // Set profile picture preview
      if (data.profilePictureUrl) {
        setProfileImagePreview(data.profilePictureUrl);
      } else {
        // Generate a default avatar based on name
        setProfileImagePreview(null);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Fallback to context data
      const user = state.user || {};
      const data = {
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        ageRange: user.ageRange || '',
        financialGoal: user.financialGoal || '',
        learningMode: user.learningMode || 'facts',
        profilePictureUrl: user.profilePictureUrl || ''
      };
      setProfileData(data);
      setOriginalData(data);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
        setProfileData(prev => ({
          ...prev,
          profilePictureUrl: reader.result // Store as base64 data URL
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (e) => {
    if (isGuest) {
      toast.error('Guest accounts cannot edit their profile. Make an actual account to save changes.');
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      const response = await UserService.updateProfile(profileData);
      
      if (response.user) {
        // Update user context
        actions.setUser({
          ...state.user,
          ...response.user
        });
        
        setOriginalData(profileData);
        setIsEditing(false);
        toast.success('Profile updated successfully!');
        soundEffects.playSuccess();
        if (e?.currentTarget) {
          createSuccessBurst(e.currentTarget);
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      // Show more detailed error message if available
      const errorMessage = error.message || 'Failed to update profile. Please try again.';
      toast.error(errorMessage);
      soundEffects.playError();
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (e) => {
    soundEffects.playClick();
    if (e) createRipple(e);
    setProfileData(originalData);
    setProfileImagePreview(originalData.profilePictureUrl || null);
    setIsEditing(false);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const getDefaultAvatarColor = (name) => {
    if (!name) return '#2563eb';
    const colors = [
      '#2563eb', '#10b981', '#f59e0b', '#ef4444', 
      '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-container content-with-nav">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-bar-content">
          <div className="top-bar-left">
            <Link 
              to="/dashboard" 
              className="back-button"
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e);
              }}
              onMouseEnter={() => soundEffects.playHover()}
            >
              <FaArrowLeft className="back-icon" />
            </Link>
            <h1 className="app-title">Profile</h1>
          </div>
        </div>
      </div>

      <div className="profile-content">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="profile-header"
        >
          <div className="profile-picture-container">
            {profileImagePreview ? (
              <img 
                src={profileImagePreview} 
                alt="Profile" 
                className="profile-picture"
              />
            ) : (
              <div 
                className="profile-picture-default"
                style={{ backgroundColor: getDefaultAvatarColor(profileData.name) }}
              >
                <span className="profile-initials">{getInitials(profileData.name)}</span>
              </div>
            )}
            
            {isEditing && (
              <label 
                className="profile-picture-edit"
                onMouseEnter={() => soundEffects.playHover()}
                onClick={() => soundEffects.playClick()}
              >
                <FaCamera className="camera-icon" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    soundEffects.playSuccess();
                    handleImageChange(e);
                  }}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>

          <h2 className="profile-name">
            {profileData.username || profileData.name || 'User'}
          </h2>
          <p className="profile-email">{profileData.email}</p>

          {isGuest && (
            <div className="guest-profile-notice">
              <p>You're browsing as a guest, so your profile can't be edited.</p>
              <button
                onClick={(e) => {
                  soundEffects.playClick();
                  createRipple(e);
                  navigate('/signup');
                }}
                onMouseEnter={(e) => {
                  soundEffects.playHover();
                  createGlow(e.currentTarget, '#d64f54', 300);
                }}
                className="guest-signup-button"
              >
                Make an Actual Account
              </button>
            </div>
          )}

          {!isEditing && !isGuest && (
            <button
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e);
                setIsEditing(true);
              }}
              onMouseEnter={(e) => {
                soundEffects.playHover();
                createGlow(e.currentTarget, '#3b82f6', 300);
              }}
              className="edit-button"
            >
              <FaEdit className="edit-icon" />
              Edit Profile
            </button>
          )}
        </motion.div>

        {/* Profile Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="profile-form-container"
        >
          <div className="profile-form">
            {/* Name Field - Private, only visible to user */}
            <div className="form-group">
              <label className="form-label">
                <FaUser className="label-icon" />
                Full Name (Private)
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={profileData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your full name (private)"
                />
              ) : (
                <div className="form-value">{profileData.name || 'Not set'}</div>
              )}
              <small className="form-help-text">Your full name is private and only visible to you</small>
            </div>

            {/* Username Field - Public */}
            <div className="form-group">
              <label className="form-label">
                <FaUser className="label-icon" />
                Username (Public)
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="username"
                  value={profileData.username}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your username"
                />
              ) : (
                <div className="form-value">{profileData.username || 'Not set'}</div>
              )}
              <small className="form-help-text">This is how others will see you on the platform</small>
            </div>

            {/* Email Field (Read-only) */}
            <div className="form-group">
              <label className="form-label">
                <FaEnvelope className="label-icon" />
                Email
              </label>
              <div className="form-value read-only">{profileData.email}</div>
            </div>

            {/* Age Range Field */}
            <div className="form-group">
              <label className="form-label">
                <FaBirthdayCake className="label-icon" />
                Age Range
              </label>
              {isEditing ? (
                <select
                  name="ageRange"
                  value={profileData.ageRange}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="">Select age range</option>
                  <option value="13-17">13-17</option>
                  <option value="18-24">18-24</option>
                  <option value="25-34">25-34</option>
                  <option value="35-44">35-44</option>
                  <option value="45-54">45-54</option>
                  <option value="55-64">55-64</option>
                  <option value="65+">65+</option>
                </select>
              ) : (
                <div className="form-value">{profileData.ageRange || 'Not set'}</div>
              )}
            </div>

            {/* Financial Goal Field */}
            <div className="form-group">
              <label className="form-label">
                <FaBullseye className="label-icon" />
                Financial Goal
              </label>
              {isEditing ? (
                <select
                  name="financialGoal"
                  value={profileData.financialGoal}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="">Select financial goal</option>
                  <option value="Save for college">Save for college</option>
                  <option value="Build emergency fund">Build emergency fund</option>
                  <option value="Save for retirement">Save for retirement</option>
                  <option value="Buy a house">Buy a house</option>
                  <option value="Start investing">Start investing</option>
                  <option value="Pay off debt">Pay off debt</option>
                  <option value="Other">Other</option>
                </select>
              ) : (
                <div className="form-value">{profileData.financialGoal || 'Not set'}</div>
              )}
            </div>

            {/* Learning Mode Field */}
            <div className="form-group">
              <label className="form-label">
                <FaBook className="label-icon" />
                Learning Mode
              </label>
              {isEditing ? (
                <select
                  name="learningMode"
                  value={profileData.learningMode}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="facts">Daily Facts</option>
                  <option value="courses">Courses</option>
                </select>
              ) : (
                <div className="form-value">
                  {profileData.learningMode === 'facts' ? 'Daily Facts' : 'Courses'}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="form-actions">
                <button
                  onClick={handleCancel}
                  onMouseEnter={() => !saving && soundEffects.playHover()}
                  className="cancel-button"
                  disabled={saving}
                >
                  <FaTimes className="button-icon" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  onMouseEnter={() => !saving && soundEffects.playHover()}
                  className="save-button"
                  disabled={saving}
                >
                  <FaSave className="button-icon" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Stats Section */}
        {state.progress && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="profile-stats"
          >
            <h3 className="stats-title">Your Progress</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">Level {state.progress.level}</div>
                <div className="stat-label">Current Level</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{state.progress.xp || 0}</div>
                <div className="stat-label">Total XP</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{state.progress.streak || 0}</div>
                <div className="stat-label">Day Streak</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{state.progress.badges?.length || 0}</div>
                <div className="stat-label">Badges Earned</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Money Bio — built from interactive workshop reflections */}
        {reflections.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="money-bio"
          >
            <h3 className="stats-title">
              <FaPenFancy style={{ marginRight: 8 }} />
              My Money Bio
            </h3>
            <p className="money-bio-subtitle">
              Written by you, during your workshops in Money Lab.
            </p>

            {reflections.some((r) => r.isGoal) && (
              <div className="money-bio-group">
                <h4 className="money-bio-group-title">
                  <FaFlag /> My Goals
                </h4>
                {reflections.filter((r) => r.isGoal).map((r) => (
                  <div key={r.bioKey} className="money-bio-card money-bio-goal">
                    <div className="money-bio-label">{r.bioLabel || r.question}</div>
                    <div className="money-bio-answer">“{r.answer}”</div>
                  </div>
                ))}
              </div>
            )}

            {reflections.some((r) => !r.isGoal) && (
              <div className="money-bio-group">
                <h4 className="money-bio-group-title">
                  <FaPenFancy /> My Reflections
                </h4>
                {reflections.filter((r) => !r.isGoal).map((r) => (
                  <div key={r.bioKey} className="money-bio-card">
                    <div className="money-bio-label">{r.bioLabel || r.question}</div>
                    <div className="money-bio-answer">“{r.answer}”</div>
                  </div>
                ))}
              </div>
            )}

            <p className="money-bio-hint">
              Update any answer by redoing its workshop in Money Lab.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Profile;









